import { useState, useMemo } from "react";
import { Search, Check } from "lucide-react";
import { Flag } from "./lawyerFlag";
import useTranslate from "../../hooks/useTranslate";
import { ChevronDownIcon } from "./v2/icons";

// A compact filter dropdown used across the lawyer directory (desktop bar and
// mobile sheet). Options are plain values; `withFlags` renders a flag-icons
// flag resolved from each value. `searchable` adds an inline filter for long
// lists (e.g. the country/jurisdiction hero filter). Closes on outside click
// via a transparent overlay.
//
// Trigger presentation rebuilt 1:1 from the V2 handoff (boards 1a/1b filter
// dropdowns): active = #EBF3FF bg / 1.5px #2283FF border / #1567D2 semibold;
// inactive = white / 1px #DDE3EC / #475467 medium; chevron-down 12px w2.5
// colored per state. The OPEN menu has no artboard (designer "Try next" note)
// so its existing presentation stays.
//
// `multi` (follow-up pack A2, board 1l implies it): `value` is an ARRAY,
// option rows toggle in/out of it and the menu stays open while picking (the
// only way to select several); "Any" clears and closes. With exactly one
// selection the trigger shows that value's label — identical to the old
// single-select trigger; with more it shows "First +N".
const FilterSelect = ({
  label,
  value,
  options = [],
  onChange,
  withFlags = false,
  searchable = false,
  fullWidth = false,
  multi = false,
  flagOptions = {}, // map value -> { code, label }
  icon = null, // optional leading glyph node (icon canon: SVG, never emoji)
}) => {
  const { t } = useTranslate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const flagFor = (v) => flagOptions[v] || {};
  const selectedList = multi ? (Array.isArray(value) ? value : []) : null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // Match the raw value AND its display label (so a country hero filter with
    // ISO-code values still matches when the user types the country name).
    return options.filter((o) => {
      const label = (flagOptions[o]?.label || o).toLowerCase();
      return o.toLowerCase().includes(q) || label.includes(q);
    });
  }, [options, query, flagOptions]);

  const labelOf = (v) => (withFlags && flagFor(v).label) || v;
  const active = multi ? selectedList.length > 0 : !!value;
  const buttonLabel = multi
    ? selectedList.length === 0
      ? label
      : selectedList.length === 1
        ? labelOf(selectedList[0])
        : `${labelOf(selectedList[0])} +${selectedList.length - 1}`
    : value
      ? labelOf(value)
      : label;
  const triggerFlagValue = multi ? selectedList[0] : value;

  return (
    <div className={`relative ${fullWidth ? "w-full" : ""}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center justify-between gap-2 ${
          fullWidth ? "w-full" : ""
        } px-3.5 py-2 rounded-[10px] text-[13px] transition-colors ${
          active
            ? "bg-[#EBF3FF] border-[1.5px] border-[#2283FF] text-[#1567D2] font-semibold"
            : "bg-white border border-[#DDE3EC] text-[#475467] font-medium hover:border-[#C9D3E3]"
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          {withFlags && active ? (
            <Flag {...flagFor(triggerFlagValue)} width={18} height={13} />
          ) : (
            icon
          )}
          <span className="truncate">{buttonLabel}</span>
        </span>
        <ChevronDownIcon
          size={12}
          className={active ? "text-[#1567D2]" : "text-[#98A2B3]"}
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full min-w-[13rem] bg-white border border-[#E6EAF2] rounded-[12px] shadow-[0_8px_28px_rgba(16,24,40,0.12)] overflow-hidden">
            {searchable && (
              <div className="p-2 border-b border-[#F0F2F6]">
                <div className="relative">
                  <Search
                    size={15}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#98A2B3]"
                  />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={label}
                    className="w-full pl-8 pr-2 py-1.5 text-[13px] rounded-[8px] border border-[#E6EAF2] focus:outline-none focus:border-[#2283FF]"
                  />
                </div>
              </div>
            )}
            <div className="max-h-60 overflow-y-auto py-1">
              {/* "Any" reset row */}
              <button
                type="button"
                onClick={() => {
                  onChange(multi ? [] : "");
                  setOpen(false);
                  setQuery("");
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] text-[#667085] hover:bg-[#F5F7FB]"
              >
                {!active && <Check size={14} className="text-[#2283FF]" />}
                <span
                  className={`inline-flex items-center gap-1.5 ${
                    !active ? "" : "pl-[22px]"
                  }`}
                >
                  {icon}
                  {label}
                </span>
              </button>

              {filtered.length === 0 ? (
                <div className="px-3 py-3 text-[13px] text-[#98A2B3]">
                  {t("No results")}
                </div>
              ) : (
                filtered.map((opt) => {
                  const selected = multi
                    ? selectedList.includes(opt)
                    : opt === value;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        if (multi) {
                          // Toggle and keep the menu open so several
                          // countries can be picked in one go.
                          onChange(
                            selected
                              ? selectedList.filter((v) => v !== opt)
                              : [...selectedList, opt]
                          );
                          return;
                        }
                        onChange(opt);
                        setOpen(false);
                        setQuery("");
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-[#F5F7FB] ${
                        selected ? "text-[#101828]" : "text-[#344054]"
                      }`}
                    >
                      {selected ? (
                        <Check size={14} className="text-[#2283FF]" />
                      ) : (
                        withFlags && flagFor(opt).code ? null : (
                          <span className="w-[14px]" />
                        )
                      )}
                      {withFlags && <Flag {...flagFor(opt)} />}
                      <span className="truncate">{labelOf(opt)}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FilterSelect;
